import React from 'react';
import { cornerCabinetFace } from '@shared-lib/kitchen-assembly/cornerCabinetFace';
import { SCHEMATIC_DETAIL_RATIO } from '../../../utils/elevationPenWeights';
import { CABINET } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';
import {
  cabinetHardwarePlacement,
  cornerDoorStyle,
  resolveHardwareType,
} from '../../3d/geometry/proceduralRenderers/cabinetFrontLayout';
import { DoorStyleInset, HardwareGlyph } from './CabinetParts';
import {
  INCH,
  Line,
  Rect,
  SchematicShell,
  ToeKickLine,
} from './SchematicPrimitives';

/**
 * Corner cabinet — L-shaped or blind, but the elevation view sees only
 * the one wall whose normal faces the camera. Render that visible face as
 * `cornerCabinetFace` divides it: a blind span another cabinet stands across,
 * and one door on the rest. Upper variants suppress toe-kick (`isUpper` lives
 * on `dimensions` for the corner cabinet, per buildCornerCabinet.js).
 *
 * An L unit has a front on EACH of its legs, split the same way (the corner
 * box at the corner end, the door on the rest). On its second leg the host
 * asks for `view: 'front'`, hands the leg's own length as the width, and sets
 * `doorDirAligned` from the unit's local +Z (`cornerLegOnWall`).
 *
 * Variant fields read:
 *   - obj.dimensions.isUpper (bool) — uppers skip toe-kick
 *   - obj.category['upper-cabinet'] — same signal, in case dims.isUpper
 *     is missing on older data
 *   - obj.dimensions.{cornerVariant, blindEnd, blindDepth, cornerBox} — the
 *     face split, read through `cornerCabinetFace`
 *   - obj.doorDirAligned — whether this wall's screen-X runs with the leg
 *     being drawn (local +X, or +Z on an L's second leg); false mirrors it
 *   - obj.doorStyle / obj.hardwareType — the run's, exactly as the 3D
 *     corner renderer wears them: the door's style inset through the shared
 *     `cornerDoorStyle` rule (glass only on an upper), the pull through
 *     `cabinetHardwarePlacement` (a knob, a bar, or nothing when handleless)
 *
 * Side views: corner cabinets are deep and largely featureless in
 * profile — render a depth×height rectangle with toe-kick + a thin
 * vertical line near the front edge as the door-edge reveal. Symmetric
 * left ↔ right.
 */

export function resolveCornerCabinetIsUpper(obj = {}) {
  if (obj?.dimensions?.isUpper === true) return true;
  if (obj?.category && obj.category['upper-cabinet']) return true;
  return false;
}

export function CornerCabinetSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  obj = {},
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const isUpper = resolveCornerCabinetIsUpper(obj);
  if (view === 'back') {
    // Behind a corner cabinet is carcass, not a door — the same finished
    // panel `CabinetBackElevation` draws for the rest of a run.
    return (
      <SchematicShell
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        {!isUpper && (
          <ToeKickLine
            width={width}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        )}
      </SchematicShell>
    );
  }
  if (view === 'left' || view === 'right') {
    return (
      <CornerCabinetSide
        depth={depth}
        height={height}
        isUpper={isUpper}
        view={view}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  // The front elevation: a span another cabinet stands across — blind, a
  // blank panel with no door — and the remainder, which carries ONE door with
  // its pull on the reachable edge. `cornerCabinetFace` owns that split (the
  // perpendicular leg's end panel for the L, the neighbouring run for a blind
  // unit); the 3D renderer and the 2D plan draw the same answer.
  //
  // It answers along the unit's LOCAL +X. Screen-X here is the wall's drawing
  // basis, which can oppose that — `doorDirAligned` is the same flag
  // `DoorElevationSchematic` mirrors its hinge on.
  const face = cornerCabinetFace(obj?.dimensions, width);
  if (!face) {
    return (
      <SchematicShell
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  const aligned = obj?.doorDirAligned !== false;
  const doorLo = aligned ? face.doorStart : width - face.doorEnd;
  const doorHi = aligned ? face.doorEnd : width - face.doorStart;
  const pullAtRight = aligned ? face.pullAtHighX : !face.pullAtHighX;
  // The blind span is on the side the pull is NOT, so the seam is the door's
  // other edge.
  const seamX = pullAtRight ? doorLo : doorHi;
  // The door sits inside its bay by the same reveal the 3D door does.
  const reveal = CABINET.doorReveal;
  const toeY = isUpper ? height : Math.max(0, height - 4 * INCH);
  const doorH = Math.max(0, toeY - 2 * reveal);
  const doorW = Math.max(0, doorHi - doorLo - 2 * reveal);
  const door = { x: doorLo + reveal, y: reveal, width: doorW, height: doorH };
  const doorStyle = cornerDoorStyle(obj?.doorStyle, isUpper);
  // `cabinetHardwarePlacement` speaks the layout's centred metres (+y up);
  // the viewBox is top-left with y down.
  const placement =
    doorH > 0 && doorW > 0
      ? cabinetHardwarePlacement(
          {
            x: door.x + doorW / 2 - width / 2,
            y: height / 2 - (door.y + doorH / 2),
            width: doorW,
            height: doorH,
            hardware: {
              isDrawer: false,
              handleOnRight: pullAtRight,
              handleAtBottom: isUpper,
            },
          },
          resolveHardwareType(obj?.hardwareType)
        )
      : null;
  const detail = {
    strokeColor,
    strokeWidth: strokeWidth * SCHEMATIC_DETAIL_RATIO,
  };
  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Corner seam: where the blind span's blank panel meets the door bay. */}
      {face.blindWidth > 0 && (
        <Line
          x1={seamX}
          y1={0}
          x2={seamX}
          y2={toeY}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
      {doorH > 0 && doorW > 0 && (
        <g data-cabinet-part="door">
          <Rect {...door} strokeColor={strokeColor} strokeWidth={strokeWidth} />
          <DoorStyleInset {...door} style={doorStyle} {...detail} />
        </g>
      )}
      {/* The pull on the door's reachable edge — away from the blind span. */}
      {placement && (
        <HardwareGlyph
          placement={{
            ...placement,
            x: placement.x + width / 2,
            y: height / 2 - placement.y,
          }}
          {...detail}
        />
      )}
      {!isUpper && (
        <ToeKickLine
          width={width}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </SchematicShell>
  );
}
CornerCabinetSchematic.supportsSideViews = true;
CornerCabinetSchematic.supportsBackView = true;

function CornerCabinetSide({
  depth,
  height,
  isUpper,
  view,
  strokeColor,
  strokeWidth,
}) {
  // Symmetric: draw "left" silhouette and mirror for "right".
  const doorEdgeX = Math.min(0.75 * INCH, depth * 0.04);
  const content = (
    <g
      transform={
        view === 'right' ? `translate(${depth}, 0) scale(-1, 1)` : undefined
      }
    >
      {/* Door-edge reveal near the front face. In the canonical "left"
          orientation the front face is at x = depth (the right edge of
          the side strip). */}
      <Line
        x1={depth - doorEdgeX}
        y1={isUpper ? 0 : 0}
        x2={depth - doorEdgeX}
        y2={isUpper ? height : Math.max(0, height - 4 * INCH)}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {!isUpper && (
        <ToeKickLine
          width={depth}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      )}
    </g>
  );
  return (
    <SchematicShell
      width={depth}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {content}
    </SchematicShell>
  );
}
